/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  Button,
  ICON_REMOVE_CIRCLE_OUTLINE,
  SelectField,
  SelectFieldProps,
  TextField,
  TextFieldProps,
} from '@squiz/sds';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { EVENT_SUBSCRIPTION_BUILDER_DATA_IDS } from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm.constants';
import {
  AttributeFilter,
  AttributeRule,
  EventSubscriptionEventType,
} from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm.defs';
import {
  EVENT_ATTRIBUTE_FILTER_OPTIONS,
  EVENT_ATTRIBUTE_FILTER_RULE_OPTIONS,
} from '../../forms/EventSubscriptionBuilderForm/EventSubscriptionBuilderForm.fixture';
import { EVENT_ATTRIBUTE_FILTER_RULES_WITHOUT_VALUE } from './EventSubscriptionAttributeFilter.fixture';
import { EventSubscriptionAttributeFilterProps } from './EventSubscriptionAttributeFilter.props';

/**
 * The event subscriptions attribute filter component.
 */
export const EventSubscriptionAttributeFilter = (props: EventSubscriptionAttributeFilterProps): JSX.Element => {
  const { t } = useTranslation();
  const { attributeFilters, eventType, fieldErrors, id, setAttributeFilters, setFieldErrors } = props;

  // Setup default properties of the attribute filter
  const emptyAttributeFilter: AttributeFilter = {
    id: window.crypto.randomUUID(),
    name: '',
    rule: '' as AttributeRule,
    value: '',
  };

  const initialAttributeFilter = attributeFilters.find((filter) => filter.id === id);
  const [attributeFilter, setAttributeFilter] = useState({
    ...(initialAttributeFilter ?? emptyAttributeFilter),
  });

  const initialValueDisabled = EVENT_ATTRIBUTE_FILTER_RULES_WITHOUT_VALUE.includes(attributeFilter.rule);
  const [isValueDisabled, setValueDisabled] = useState(initialValueDisabled);

  useEffect((): void => {
    if (attributeFilter.name) {
      const validAttributeSelectOptions = EVENT_ATTRIBUTE_FILTER_OPTIONS[eventType as EventSubscriptionEventType] ?? [];
      const validAttributes = validAttributeSelectOptions.map((option) => option.value);
      const hasIncompatibleAttribute = !validAttributes.includes(attributeFilter.name);

      if (hasIncompatibleAttribute) {
        setAttributeFilter({ ...attributeFilter, name: '' });
        return;
      }
    }

    const isValueNotRequired = EVENT_ATTRIBUTE_FILTER_RULES_WITHOUT_VALUE.includes(attributeFilter.rule);
    if (isValueNotRequired) {
      if (attributeFilter.value) {
        setAttributeFilter({ ...attributeFilter, value: '' });
      }
      setValueDisabled(true);
    } else {
      setValueDisabled(false);
    }
  }, [attributeFilter, eventType, setValueDisabled]);

  // Only update event properties if data is out of sync
  useEffect(() => {
    const filterFound = attributeFilters.find((filter) => filter.id === id);
    if (!filterFound) {
      return;
    }

    const { name, rule, value } = filterFound;
    const needsUpdate =
      name !== attributeFilter.name ||
      rule !== attributeFilter.rule ||
      value !== attributeFilter.value ||
      (value && isValueDisabled);

    if (needsUpdate) {
      const updatedAttributeFilters = attributeFilters.map((filterToUpdate) => {
        if (filterToUpdate.id === id) {
          filterToUpdate = {
            ...filterToUpdate,
            name: attributeFilter.name,
            rule: attributeFilter.rule,
            value: isValueDisabled ? '' : attributeFilter.value,
          };
        }

        return filterToUpdate;
      });

      setAttributeFilters(updatedAttributeFilters);
    }
  }, [attributeFilter, attributeFilters, eventType, id, isValueDisabled, setAttributeFilters]);

  // Refs
  const attributeInputRef = useRef<HTMLInputElement>(null);
  const ruleInputRef = useRef<HTMLInputElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);

  // Labels
  const labels = useMemo(() => {
    return {
      attributeRequired: t(CONFIGURATION_STRINGS.attributeRequired),
      deleteFilter: t(CONFIGURATION_STRINGS.deleteFilter),
      requiredField: t(CONFIGURATION_STRINGS.requiredField),
      ruleRequired: t(CONFIGURATION_STRINGS.ruleRequired),
      valueRequired: t(CONFIGURATION_STRINGS.valueRequired),
    };
  }, [t]);

  const validateFields = useCallback(
    (fieldId: string, value: string | null): void => {
      const nextErrors = { ...fieldErrors };
      delete nextErrors[fieldId];

      if (!value && !isValueDisabled) {
        nextErrors[fieldId] = labels.requiredField;
      }

      setFieldErrors(nextErrors);
    },
    [fieldErrors, isValueDisabled, labels, setFieldErrors],
  );

  const attributeFieldProps: SelectFieldProps = {
    callback: (option) => {
      setAttributeFilter({ ...attributeFilter, name: option.value });
      validateFields(`event-subscription-attribute-name-${id}`, option.value);
    },
    classes: 'event-subscription-attribute',
    disabled: eventType === null,
    errorMessage: fieldErrors[`event-subscription-attribute-name-${id}`],
    fieldLabel: labels.attributeRequired,
    id: `event-subscription-attribute-name-${id}`,
    name: `event-subscription-attribute-name-${id}`,
    options: EVENT_ATTRIBUTE_FILTER_OPTIONS[eventType as EventSubscriptionEventType] ?? [],
    value: attributeFilter.name,
  };

  const ruleFieldProps: SelectFieldProps = {
    callback: (option) => {
      setAttributeFilter({ ...attributeFilter, rule: option.value as AttributeRule });
      validateFields(`event-subscription-rule-${id}`, option.value);
    },
    classes: 'event-subscription-rule',
    errorMessage: fieldErrors[`event-subscription-rule-${id}`],
    fieldLabel: labels.ruleRequired,
    id: `event-subscription-rule-${id}`,
    name: `event-subscription-rule-${id}`,
    options: EVENT_ATTRIBUTE_FILTER_RULE_OPTIONS,
    value: attributeFilter.rule,
  };

  const valueFieldProps: TextFieldProps = {
    classes: 'event-subscription-value',
    customChange: (event: ChangeEvent<HTMLInputElement>): void => {
      setAttributeFilter({ ...attributeFilter, value: event.target.value });
      validateFields(`event-subscription-value-${id}`, event.target.value);
    },
    disabled: isValueDisabled,
    errorMessage: isValueDisabled ? null : fieldErrors[`event-subscription-value-${id}`],
    fieldLabel: labels.valueRequired,
    id: `event-subscription-value-${id}`,
    name: `event-subscription-value-${id}`,
    value: attributeFilter.value as string,
  };

  return (
    <div className="event-subscription-attribute-filters__row">
      <SelectField
        {...attributeFieldProps}
        data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.attributeFilterName}
        ref={attributeInputRef}
      />
      <SelectField
        {...ruleFieldProps}
        data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.attributeFilterRule}
        ref={ruleInputRef}
      />
      <TextField
        {...valueFieldProps}
        data-id={EVENT_SUBSCRIPTION_BUILDER_DATA_IDS.attributeFilterValue}
        ref={valueInputRef}
      />
      <div className="event-subscription-attribute-filters__remove">
        <Button
          format={BUTTON_FORMAT_TRANSPARENT}
          icon={ICON_REMOVE_CIRCLE_OUTLINE}
          label={labels.deleteFilter}
          onClick={(): void => {
            const updatedAttributeFilters = attributeFilters.filter((filter) => filter.id !== attributeFilter.id);
            setAttributeFilters(updatedAttributeFilters);
          }}
          theme={BUTTON_THEME_DEFAULT}
        />
      </div>
    </div>
  );
};
